import { cn } from '@/lib/utils'
import { Button, IconButton } from './Button'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  showFirstLast?: boolean
  className?: string
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true,
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null

  // Calculate visible page numbers
  const getVisiblePages = () => {
    const delta = 2 // Pages to show on each side of current
    const pages: (number | 'ellipsis')[] = []

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        pages.push(i)
      } else if (pages[pages.length - 1] !== 'ellipsis') {
        pages.push('ellipsis')
      }
    }

    return pages
  }

  const visiblePages = getVisiblePages()

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex items-center justify-center gap-1', className)}
    >
      {/* First page */}
      {showFirstLast && (
        <IconButton
          icon="solar:double-alt-arrow-left-line-duotone"
          label="First page"
          variant="ghost"
          size="sm"
          disabled={currentPage === 1}
          onClick={() => onPageChange(1)}
        />
      )}

      {/* Previous page */}
      <IconButton
        icon="solar:alt-arrow-left-line-duotone"
        label="Previous page"
        variant="ghost"
        size="sm"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      />

      {/* Page numbers */}
      <div className="flex items-center gap-1 mx-2">
        {visiblePages.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-2 text-slate-400"
              >
                ...
              </span>
            )
          }

          const isActive = page === currentPage

          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={cn(
                'min-w-8 h-8 px-2 text-sm font-medium rounded-lg transition-colors',
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              {page}
            </button>
          )
        })}
      </div>

      {/* Next page */}
      <IconButton
        icon="solar:alt-arrow-right-line-duotone"
        label="Next page"
        variant="ghost"
        size="sm"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      />

      {/* Last page */}
      {showFirstLast && (
        <IconButton
          icon="solar:double-alt-arrow-right-line-duotone"
          label="Last page"
          variant="ghost"
          size="sm"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(totalPages)}
        />
      )}
    </nav>
  )
}

// Simple prev/next pagination
interface SimplePaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
}

export function SimplePagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
}: SimplePaginationProps) {
  if (totalPages <= 1) return null

  return (
    <div className={cn('flex items-center justify-between', className)}>
      <Button
        variant="outline"
        size="sm"
        icon="solar:alt-arrow-left-line-duotone"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Previous
      </Button>

      <span className="text-sm text-slate-600">
        Page {currentPage} of {totalPages}
      </span>

      <Button
        variant="outline"
        size="sm"
        icon="solar:alt-arrow-right-line-duotone"
        iconPosition="right"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </Button>
    </div>
  )
}

// Page info display
interface PageInfoProps {
  currentPage: number
  pageSize: number
  totalItems: number
  className?: string
}

export function PageInfo({
  currentPage,
  pageSize,
  totalItems,
  className,
}: PageInfoProps) {
  const safePageSize = Math.max(pageSize, 1)

  const [start, end] = (() => {
    if (totalItems <= 0) return [0, 0]

    const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize))
    const safePage = Math.min(Math.max(currentPage, 1), totalPages)
    const rangeStart = (safePage - 1) * safePageSize + 1
    const rangeEnd = Math.min(safePage * safePageSize, totalItems)

    return [rangeStart, rangeEnd]
  })()

  return (
    <p className={cn('text-sm text-slate-600', className)}>
      Showing <span className="font-medium">{start}</span> to{' '}
      <span className="font-medium">{end}</span> of{' '}
      <span className="font-medium">{totalItems.toLocaleString()}</span> results
    </p>
  )
}
